/**
 * PhotoHeroResult - Full-screen photo-hero layout for displaying a single dice result.
 *
 * The captured photo fills the container as the background, with the dice value,
 * confidence, and action buttons floating as transparent glass-morphism overlays.
 *
 * @module PhotoHeroResult
 */

import { useEffect, useMemo, useState } from 'react';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { FrogMascot } from '@/components/FrogMascot';
import { ValueEditSheet } from '@/components/ValueEditSheet';
import { cn } from '@/lib/utils';
import { getDiceMax } from '@/services/recognition';

/** Props for the PhotoHeroResult component. */
export interface PhotoHeroResultProps {
  /** Captured image as data URL (used as background and lightbox source) */
  imageData: string;
  /** Detected dice value */
  value: number;
  /** Dice type string (e.g. "d20") */
  diceType: string;
  /** Recognition confidence 0-1 */
  confidence: number;
  /** Whether the value was manually edited */
  wasEdited?: boolean;
  /** Original value before edit */
  originalValue?: number;
  /** Animation state for entry effects */
  animationState?: 'entering' | 'displaying' | 'sending' | 'idle';
  /** Called when animation completes */
  onAnimationComplete?: () => void;
  /** Action buttons rendered in floating bottom bar */
  actions: React.ReactNode;
  /** Called when user wants to edit the value */
  onValueEdit?: (newValue: number) => void;
  /** Optional additional CSS class names */
  className?: string;
  /** Capture kind: 'image' (default) or 'video' (TICKET-281c) */
  kind?: 'image' | 'video';
  /** Recorded video blob — required when `kind === 'video'` */
  videoBlob?: Blob;
}

/**
 * Returns the Tailwind text color class for a confidence value.
 * Green for high confidence, amber for medium, red for low.
 */
function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'text-green-400';
  if (confidence >= 0.7) return 'text-amber-400';
  return 'text-red-400';
}

/**
 * Full-screen photo-hero layout for displaying a single dice result.
 *
 * The captured photo fills the background with glass-morphism overlays for
 * dice type, confidence, value (tappable to edit), nat20/nat1 reactions,
 * and an action bar at the bottom.
 */
export function PhotoHeroResult({
  imageData,
  value,
  diceType,
  confidence,
  wasEdited,
  originalValue,
  animationState,
  onAnimationComplete,
  actions,
  onValueEdit,
  className,
  kind = 'image',
  videoBlob
}: PhotoHeroResultProps) {
  const [imageExpanded, setImageExpanded] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  // Video-mode object URL (TICKET-281c). Created from the recorded blob
  // and revoked on unmount / blob change to avoid leaks.
  const videoObjectUrl = useMemo(
    () => (kind === 'video' && videoBlob ? URL.createObjectURL(videoBlob) : null),
    [kind, videoBlob]
  );
  useEffect(() => {
    return () => {
      if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    };
  }, [videoObjectUrl]);

  const maxValue = getDiceMax(diceType);
  const isNat20 = value === 20 && maxValue === 20;
  const isNat1 = value === 1 && maxValue >= 4;

  const animationClass =
    animationState === 'entering'
      ? 'animate-hop-appear'
      : animationState === 'sending'
        ? 'animate-button-release'
        : '';

  const valuePill = (
    <div
      className={cn(
        'backdrop-blur-sm bg-black/60 rounded-2xl px-8 py-4 border border-white/20',
        'flex flex-col items-center',
        animationClass
      )}
      onAnimationEnd={() => {
        if (animationState === 'sending') {
          onAnimationComplete?.();
        }
      }}
    >
      <span className="text-5xl font-bold text-white text-glow">{value}</span>
      {wasEdited && originalValue !== undefined && (
        <span className="text-xs text-white/70">was {originalValue}</span>
      )}
      {onValueEdit && (
        <span className="text-[10px] text-white/40 mt-1">tap to edit</span>
      )}
    </div>
  );

  return (
    <div className={cn('relative h-full w-full overflow-hidden rounded-lg', className)}>
      {/* Photo / Video background */}
      {kind === 'video' && videoObjectUrl ? (
        <video
          src={videoObjectUrl}
          className="absolute inset-0 w-full h-full object-cover bg-black"
          controls
          playsInline
          muted
          loop
          autoPlay
          aria-label="Captured dice video"
        />
      ) : (
        <button
          type="button"
          className="absolute inset-0 w-full h-full cursor-pointer bg-black"
          onClick={() => imageData && setImageExpanded(true)}
          aria-label="View fullscreen photo"
        >
          {imageData && (
            <img
              src={imageData}
              alt="Captured dice"
              className="h-full w-full object-cover"
            />
          )}
        </button>
      )}

      {/* Top overlay: dice type + confidence */}
      <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none p-3">
        <div className="flex items-start justify-between">
          <span className="pointer-events-auto backdrop-blur-sm bg-black/50 text-white text-xs font-medium uppercase tracking-wide rounded-full px-3 py-1.5">
            {diceType}
          </span>
          <span
            className={cn(
              'pointer-events-auto backdrop-blur-sm bg-black/50 text-xs rounded-full px-3 py-1.5',
              getConfidenceColor(confidence)
            )}
          >
            {Math.round(confidence * 100)}%
          </span>
        </div>
      </div>

      {/* Nat20 / Nat1 frog reaction */}
      {(isNat20 || isNat1) && (
        <div className="absolute left-1/2 -translate-x-1/2 z-10 bottom-44">
          <div className="backdrop-blur-sm bg-black/50 rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
            <FrogMascot variant={isNat20 ? 'lucky' : 'unlucky'} size="sm" />
            <span className="text-white text-xs font-medium">
              {isNat20 ? 'Natural 20!' : 'Natural 1...'}
            </span>
          </div>
        </div>
      )}

      {/* Value pill (tappable to edit) */}
      <div className="absolute left-1/2 -translate-x-1/2 z-10 bottom-32">
        {onValueEdit ? (
          <button
            type="button"
            onClick={() => setEditSheetOpen(true)}
            aria-label={`Edit dice value: ${value}`}
          >
            {valuePill}
          </button>
        ) : (
          valuePill
        )}
      </div>

      {/* Action bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-4 backdrop-blur-md bg-black/50 border-t border-white/10">
        {actions}
      </div>

      {/* Lightbox for fullscreen photo view */}
      <ImageLightbox
        imageUrl={imageData}
        alt="Captured dice fullscreen"
        open={imageExpanded}
        onClose={() => setImageExpanded(false)}
      />

      {/* Value edit sheet */}
      {onValueEdit && (
        <ValueEditSheet
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
          value={value}
          diceType={diceType}
          onValueEdit={onValueEdit}
        />
      )}
    </div>
  );
}
